import type { TAGULAR_ELEMENT } from "@/types/tagular";
import classNames from "classnames";
import dynamic from "next/dynamic";
import React from "react";

const LEGAL_TOOLTIP_TOKEN_PATTERN = /\{(?:legal\.)?tooltip\}/;

const LegalTooltipContent = dynamic(() => import("./LegalTooltipContent"));

interface LegalDisclaimerProps {
  disclaimers: string | string[];
  className?: string;
  testId?: string;
  variant?: "compact" | "spaced" | "inline";
  maxWidth?: string;
  textAlign?: "left" | "center" | "right";
  tooltip?: string;
  tooltipAriaLabel?: string;
  tagularProps?: TAGULAR_ELEMENT;
}

const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({
  disclaimers,
  className = "",
  testId = "legal-copy-text",
  variant = "spaced",
  maxWidth,
  textAlign = "left",
  tooltip,
  tooltipAriaLabel,
  tagularProps,
}) => {
  // Normalize disclaimers to array
  const disclaimerArray = Array.isArray(disclaimers)
    ? disclaimers
    : [disclaimers];

  // Handle empty arrays
  if (disclaimerArray.length === 0) {
    return null;
  }

  const getVariantClasses = () => {
    switch (variant) {
      case "compact":
        return "space-y-1";
      case "inline":
        return "space-y-0";
      case "spaced":
      default:
        return "space-y-2";
    }
  };

  const getTextAlign = () => {
    switch (textAlign) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      case "left":
      default:
        return "text-left";
    }
  };

  const containerClasses = classNames(
    "text-caption",
    getVariantClasses(),
    getTextAlign(),
    className
  );

  const containerStyle = maxWidth ? { maxWidth } : undefined;
  const renderDisclaimerContent = (disclaimer: string) => {
    const shouldRenderTooltipContent =
      !!tooltip?.trim() || LEGAL_TOOLTIP_TOKEN_PATTERN.test(disclaimer);

    if (shouldRenderTooltipContent) {
      return (
        <LegalTooltipContent
          copy={disclaimer}
          tooltip={tooltip}
          tooltipAriaLabel={tooltipAriaLabel}
          tagularProps={tagularProps}
        />
      );
    }

    return <span dangerouslySetInnerHTML={{ __html: disclaimer }} />;
  };

  if (variant === "inline") {
    // For inline variant, join disclaimers with spaces
    const inlineContent = disclaimerArray.join(" ");
    return (
      <div
        className={containerClasses}
        data-testid={testId}
        style={containerStyle}
      >
        {renderDisclaimerContent(inlineContent)}
      </div>
    );
  }

  return (
    <div
      className={containerClasses}
      data-testid={testId}
      style={containerStyle}
    >
      {disclaimerArray.map((disclaimer, index) => (
        <div key={index}>{renderDisclaimerContent(disclaimer)}</div>
      ))}
    </div>
  );
};

export default LegalDisclaimer;
